import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { InfiniteResponse } from "hooks/useManageInfiniteScroll";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import useSWRInfinite from "swr/infinite";
import { ListingProps, ListResponse } from "types/listing";
import {
  TransactionBackendModel,
  TransactionFrontendModel,
} from "types/transactions";
import { getEnvVariable } from "utilities/env";

export function useTransactions() {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher } = useAuth();

  const useTransactionsList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<TransactionBackendModel>> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/transaction${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const useInfiniteTransactionsList = (props?: ListingProps) => {
    return useSWRInfinite<InfiniteResponse<TransactionBackendModel>>(
      (index) =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/transaction${querystring({ ...props, page: index + 1 })}`
          : null,
      fetcher,
      { revalidateFirstPage: false },
    );
  };

  const useTransactionData = (
    transactionID: string,
  ): SWRResponse<TransactionBackendModel> => {
    return useSWR(
      () =>
        code && transactionID
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/transaction/${transactionID}`
          : null,
      fetcher,
    );
  };

  const addTransaction = async (values: TransactionFrontendModel) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/transaction`,
      {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transaction: values }),
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const editTransaction = async (
    transactionID: string,
    values: TransactionFrontendModel,
  ) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/transaction/${transactionID}`,
      {
        method: "put",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transaction: values }),
      },
    );

    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const assignTransaction = async (values: {
    customerId: string;
    transactionDocumentNumber: string;
  }) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/transaction/assign`,
      {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assign: values }),
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const importMatches = async (file: any) => {
    const formData = new FormData();
    formData.append("file[file]", file);
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/transaction/assign/import`,
      { body: formData, method: "post" },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const importTransaction = async (file: any) => {
    const formData = new FormData();
    formData.append("file[file]", file);
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/transaction/import`,
      { body: formData, method: "post" },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  return {
    addTransaction,
    assignTransaction,
    editTransaction,
    importMatches,
    importTransaction,
    useTransactionData,
    useTransactionsList,
    useInfiniteTransactionsList,
  };
}
